import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, Clock, CreditCard, KeyRound, LogOut, Ticket, UserCircle2 } from "lucide-react";
import { api, formatDate, isAdmin, mediaUrl, money } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Shell } from "@/components/layout/Shell";
import { PaymentStep } from "@/pages/auth/RegisterPage";
import { ProfileEditor } from "./ProfileEditor";
import { AnnouncementsFeed } from "./AnnouncementsFeed";

const STATUS = {
  approved: ["Active member", "success"], pending: ["Awaiting activation", "warn"], rejected: ["Application declined", "danger"], suspended: ["Account suspended", "danger"]
};
const PAY = { awaiting_payment: "Awaiting payment", submitted: "Under verification", paid: "Paid", rejected: "Rejected" };

const ChangePassword = ({ hasPassword, onDone }) => {
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async e => {
    e.preventDefault(); setErr(""); setMsg("");
    try { const r = await api("/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: "", ...form }) }); setMsg(hasPassword ? r.message : "Password set. You can now sign in with email & password too."); setForm({}); onDone?.(); }
    catch (ex) { setErr(ex.message); }
  };
  return (
    <form className="account-card" onSubmit={submit} data-testid="change-password-form">
      <h3><KeyRound size={18} /> {hasPassword ? "Change password" : "Set a password"}</h3>
      {!hasPassword && <div className="hint" data-testid="set-password-hint">You signed in with Google. Set a password to also sign in with your email on any device.</div>}
      {hasPassword && <label>Current password<input type="password" value={form.current_password || ""} onChange={e => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" /></label>}
      <label>New password<input type="password" required minLength={8} value={form.new_password || ""} onChange={e => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" /></label>
      {msg && <div className="success-message" data-testid="password-success">{msg}</div>}
      {err && <div className="error-message" data-testid="password-error">{err}</div>}
      <button className="button button-ghost dark" data-testid="change-password-submit">{hasPassword ? "Update password" : "Set password"}</button>
    </form>
  );
};

export default function AccountPage() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [paying, setPaying] = useState(params.get("renew") === "1");

  const load = () => api("/account/me").then(setData).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { if (user === false) nav("/login", { replace: true }); }, [user, nav]);

  if (!user || !data) return <div className="loading" data-testid="account-loading">Loading your dashboard…</div>;
  const { member, payments, registrations, plans, payment_instructions } = data;
  const u = data.user;
  const latest = payments[0];
  const needsPayment = u.role === "member" && !["approved", "rejected", "suspended"].includes(u.status) && (!latest || ["awaiting_payment", "rejected"].includes(latest.status));
  const renewalDue = data.renewal_due && latest?.status !== "submitted";
  const plan = plans.find(p => p.key === (latest?.plan_key || u.plan_key)) || plans[0];
  const [statusLabel, tone] = STATUS[u.status] || STATUS.pending;

  return (
    <Shell>
      <main className="inner-page account-page">
        <div className="container">
          <div className="account-head">
            <div>
              <span className="eyebrow">Member dashboard</span>
              <h1>Hello, <span className="gold-text">{u.name?.split(" ")[0] || "member"}.</span></h1>
            </div>
            <div className="admin-toolbar-actions">
              {isAdmin(u) && <Link to="/admin" className="button button-ghost dark" data-testid="account-admin-link">Admin panel</Link>}
              <button className="button button-ghost dark" onClick={() => logout().then(() => nav("/"))} data-testid="account-logout-button"><LogOut size={14} /> Sign out</button>
            </div>
          </div>

          <div className={`status-banner ${tone}`} data-testid="account-status-banner">
            <BadgeCheck size={20} />
            <div>
              <b>{statusLabel}</b>
              <span>{u.status === "approved" ? `Membership active${u.membership_valid_until ? ` until ${formatDate(u.membership_valid_until)}` : " · lifetime"}.${renewalDue ? " Renew now to stay active without interruption." : ""}` : u.status === "rejected" ? `The admin could not approve your application${u.review_note ? ` — ${u.review_note}` : ""}. Please contact the association office if you have questions.` : latest?.status === "submitted" ? "Your payment is being verified by the treasurer. You'll be activated shortly." : latest?.status === "rejected" ? `Payment could not be verified${latest.review_note ? ` — ${latest.review_note}` : ""}. Please resubmit.` : latest?.status === "paid" || !needsPayment ? "Your application is with the DCEA admin for approval. Decisions are made within 24 hours and you'll be notified by email." : "Complete your membership payment to activate your account. The admin reviews every application within 24 hours."}</span>
            </div>
            {needsPayment && !paying && <button className="button button-gold" onClick={() => setPaying(true)} data-testid="account-pay-button"><CreditCard size={15} /> Complete payment</button>}
            {renewalDue && !paying && <button className="button button-gold" onClick={() => setPaying(true)} data-testid="account-renew-button"><CreditCard size={15} /> Renew membership</button>}
          </div>

          {paying && (needsPayment || renewalDue) && plan && (
            <div className="account-pay" data-testid="account-pay-step">
              <PaymentStep plan={plan} instructions={payment_instructions} onDone={() => { setPaying(false); load(); }} />
            </div>
          )}

          <div className="account-grid">
            <AnnouncementsFeed />
            <section className="account-card member-card" data-testid="member-id-card">
              <span className="card-kicker">Membership card</span>
              <div className="member-card-head">{u.picture && <img src={mediaUrl(u.picture)} alt="" className="member-avatar" data-testid="member-card-avatar" />}<h2>{u.name}</h2></div>
              <div className="member-id"><span>Member ID</span><strong data-testid="member-id-value">{member?.member_id || "Issued after activation"}</strong></div>
              <div className="member-facts">
                <span><UserCircle2 size={14} /> {u.email}</span>
                <span><CreditCard size={14} /> {u.membership_plan || plan?.label || "—"}</span>
                <span><Clock size={14} /> Joined {formatDate(u.created_at)}</span>
              </div>
            </section>

            <section className="account-card" data-testid="payments-card">
              <h3><CreditCard size={18} /> Payments</h3>
              {payments.length ? payments.map(p => (
                <div className="list-row" key={p.id} data-testid={`payment-row-${p.id}`}>
                  <div><b>{p.plan_label}</b><small>{p.reference ? `Ref ${p.reference}` : "No reference yet"}{p.proof_url && <> · <a href={mediaUrl(p.proof_url)} target="_blank" rel="noreferrer">receipt</a></>}</small></div>
                  <strong>{money(p.amount, p.currency)}</strong>
                  <span className={`pill ${p.status}`}>{PAY[p.status] || p.status}</span>
                </div>
              )) : <div className="hint">No payments yet.</div>}
            </section>

            <section className="account-card" data-testid="registrations-card">
              <h3><Ticket size={18} /> Event registrations</h3>
              {registrations.length ? registrations.map(r => (
                <div className="list-row" key={r.id} data-testid={`registration-row-${r.id}`}>
                  <div><b>{r.event_title || r.event_id}</b><small>{r.event_date ? formatDate(r.event_date) : ""} · Ref {r.id}{r.checkin_code && <> · <Link to={`/ticket/${r.checkin_code}`} data-testid={`ticket-link-${r.id}`}>QR ticket</Link></>}</small></div>
                  <span className={`pill ${r.checked_in_at ? "paid" : ""}`}>{r.status}</span>
                </div>
              )) : <div className="hint">You haven't registered for any events yet. <Link to="/events" data-testid="account-browse-events">Browse events <ArrowRight size={12} /></Link></div>}
            </section>

            <ChangePassword hasPassword={(u.auth_providers || []).includes("password")} onDone={() => { load(); refresh(); }} />
            <ProfileEditor user={u} onSaved={() => { load(); refresh(); }} />
          </div>
          <div style={{ height: 100 }} />
        </div>
      </main>
    </Shell>
  );
}
